import React, { useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
  Chip,
  Typography,
} from "@mui/material";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { groupBySession, formatDuration } from "./aggregate";

function formatWhen(ts) {
  if (!ts) return "—";
  return String(ts).slice(0, 16).replace("T", " ");
}

const ellipsis = {
  maxWidth: 200,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// One collapsed session row; expands to reveal the page views within it.
function SessionRow({ s }) {
  const [open, setOpen] = useState(false);
  const location = [s.city, s.state].filter(Boolean).join(", ") || "—";
  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: "pointer", "& > *": { borderBottom: open ? "unset" : undefined } }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? "collapse session" : "expand session"}>
            {open ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowRightRoundedIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatWhen(s.start)}</TableCell>
        <TableCell>
          <Chip size="small" color="primary" label={`${s.count} page${s.count === 1 ? "" : "s"}`} />
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDuration(s.durationMs)}</TableCell>
        <TableCell>{s.device}</TableCell>
        <TableCell>{s.browser}</TableCell>
        <TableCell>{s.os}</TableCell>
        <TableCell>{location}</TableCell>
        <TableCell sx={ellipsis} title={s.referrer || "Direct"}>
          {s.referrer || "Direct"}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1, ml: { xs: 1, sm: 5 }, mb: 2 }}>
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Pages in this session
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell>Title</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s.events.map((ev, i) => (
                    <TableRow key={ev.apiid || i}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatWhen(ev.EventTimestampUtc)}
                      </TableCell>
                      <TableCell sx={ellipsis} title={ev.Path}>
                        {ev.Path || "—"}
                      </TableCell>
                      <TableCell sx={ellipsis} title={ev.PageTitle}>
                        {ev.PageTitle || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function SessionsTable({ events, maxSessions = 200 }) {
  const sessions = groupBySession(events).slice(0, maxSessions);

  if (sessions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No sessions in this range yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Started</TableCell>
            <TableCell>Pages</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Device</TableCell>
            <TableCell>Browser</TableCell>
            <TableCell>OS</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Source</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map((s) => (
            <SessionRow key={s.sessionId} s={s} />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
